import { useState, type FormEvent } from "react";

import { lanUnlock } from "../api/auth.js";
import { ApiRequestError } from "../api/client.js";
import { useApiClient } from "../api/ApiClientContext.js";
import { describeApiError } from "../domain/errors.js";
import "./login.css";

/**
 * The gate a **desktop LAN device** sees. The till itself (loopback) never
 * reaches this — its capabilities come back `lan.locked: false` and it goes
 * straight into the app, login-free, exactly as a single till always has. A
 * device coming in over the shop network gets `lan.locked: true` instead
 * (`apps/server/src/routes/capabilities.ts`) and lands here until it enters the
 * one shared passphrase.
 *
 * On success the server sets the LAN cookie and `onUnlocked` re-fetches
 * capabilities — which now report unlocked — so the normal shell renders. This
 * is intentionally *not* the account `LoginPage`: there are no accounts on a
 * desktop deployment, only the shared passphrase.
 */
export function LanUnlockPage({ onUnlocked }: { onUnlocked: () => void }) {
  const client = useApiClient();
  const [passphrase, setPassphrase] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await lanUnlock(client, passphrase);
      onUnlocked();
    } catch (err) {
      // A wrong passphrase (401) gets its own wording; everything else
      // (rate-limited, network) falls back to the shared translator.
      const isWrong = err instanceof ApiRequestError && err.failure.kind === "unauthorized";
      setError(isWrong ? "Napačno geslo." : describeApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <form className="login-card" onSubmit={(e) => void handleSubmit(e)}>
        <h1>Dostop do omrežja</h1>
        <p className="login-notice">Ta naprava dostopa prek omrežja. Vnesite skupno geslo za dostop.</p>
        <label htmlFor="lan-passphrase">Geslo za omrežje</label>
        <input
          id="lan-passphrase"
          type="password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          autoComplete="current-password"
          autoFocus
          required
          disabled={submitting}
        />
        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" disabled={submitting}>
          {submitting ? "Odklepanje …" : "Vstopi"}
        </button>
      </form>
    </main>
  );
}
